// Orchestrates one scan: image -> OCR -> parse -> items in the store.
// Prefers the on-device LLM when present, always falls back to the heuristic.

import { store } from "../models/store.js";
import { makeScanCanvas } from "./image.js";
import { runOcr } from "./ocr.js";
import { parseReceipt } from "./parser.js";
import { llmAvailable, parseWithLlm } from "./llm.js";
import type { ParseResult } from "../models/types.js";

/** Run a full scan. If `useRegion` is true and a box was drawn, only that part
 *  of the image is read. */
export async function scan(useRegion = false): Promise<void> {
  const img = store.imageEl;
  if (!img) return;

  store.setPhase("ocr", "Reading the receipt…");
  const canvas = makeScanCanvas(img, useRegion ? store.region : null);

  let text: string;
  try {
    text = await runOcr(canvas, (fraction) =>
      store.setStatus(`Reading the receipt… ${Math.round(fraction * 100)}%`),
    );
  } catch (err) {
    store.setPhase("loaded", err instanceof Error ? err.message : "OCR failed.");
    return;
  }

  store.ocrText = text;
  store.setPhase("parsing", "Finding items…");

  let result: ParseResult;
  if (await llmAvailable()) {
    try {
      result = await parseWithLlm(text);
    } catch {
      result = parseReceipt(text);
    }
  } else {
    result = parseReceipt(text);
  }

  store.applyParse(result);

  if (!result.items.length) {
    store.setStatus(
      "No items detected. Try drawing a box around the items, or add them by hand.",
    );
  } else {
    const how = result.method === "llm" ? "on-device AI" : "text parsing";
    const count = result.items.length;
    const plural = count === 1 ? "" : "s";
    store.setStatus(
      `Found ${count} item${plural} with ${how}. Tick what you're paying for.`,
    );
  }
}
